import { computePeaks } from './waveformPeaks';

/**
 * Seek-bar waveform peaks for a library video/audio file, computed in the
 * browser from the original bytes — PROGRESSIVELY: every time a larger
 * prefix of the file is available (as a download lands, as a v2 part
 * decrypts) the prefix is decoded and its peaks placed on the left of the
 * timeline, so the waveform draws in from the start and fills to the
 * right rather than appearing all at once at the end. Browsers decode a
 * truncated prefix of faststart mp4, webm, mp3 and wav; a container with
 * its index at the end (a non-faststart m4a) only decodes once complete,
 * and simply shows up then.
 *
 * Decoding goes through an OfflineAudioContext (the decode runs off the
 * main thread inside the browser) and the PCM→peaks reduction runs in a
 * worker. Results are memoised per (upload, version) for the session.
 *
 * Every failure path resolves to "no waveform" (a video with no audio
 * track, a codec the browser can't decode, a worker that won't spawn), and
 * the seek bar draws without one.
 */

/** Peak columns per file. The bar scales these to its width; more than a
 *  few hundred is invisible at any size the preview renders at. */
export const WAVEFORM_BUCKETS = 512;
/** Decode at a low rate: a peak envelope needs shape, not fidelity, and a
 *  decoded hour of stereo at 48 kHz would be ~700 MB of floats. At 8 kHz the
 *  same hour is ~115 MB, which is the ceiling we accept. */
const DECODE_SAMPLE_RATE = 8000;
/** Sources above this are not decoded for a waveform (the bytes would be
 *  held twice: once for the trim, once detached into the decoder). Matches
 *  the preview's existing in-browser decrypt ceiling for audio. */
export const WAVEFORM_MAX_SOURCE_BYTES = 200 * 1024 * 1024;
export const WAVEFORM_MAX_DURATION_S = 60 * 60;
/** A prefix is re-decoded once it has at least doubled since the last
 *  decode (first at this share of the file), and never more often than
 *  PROGRESSIVE_MIN_INTERVAL_MS. Each decode re-reads the whole prefix, so
 *  doubling (10 → 20 → 40 → 80 → 100 %) keeps the total work at ~2.5× one
 *  full decode instead of ~5.5× for fixed steps — the difference between a
 *  waveform that fills in and one that steals the decoder from playback. */
const PROGRESSIVE_FIRST_STEP = 0.1;
const PROGRESSIVE_GROWTH = 2;
const PROGRESSIVE_MIN_INTERVAL_MS = 2000;
/** Session cache — each entry is a few KB, so this is generous. */
const CACHE_MAX_ENTRIES = 48;

export interface WaveformState {
  /** Full-length peak array; only the first `coverage` of it is meaningful. */
  peaks: Float32Array | null;
  /** 0..1 of the timeline the peaks cover so far (1 = complete). */
  coverage: number;
  /** No more updates will come (complete, or decided there is no waveform). */
  final: boolean;
}

type Listener = (state: WaveformState) => void;

interface Entry {
  state: WaveformState;
  listeners: Set<Listener>;
  /** Fraction of the file the last decode covered (bytes, not time). */
  decodedFraction: number;
  decodedAt: number;
  /** Serialises decodes for one key; the newest pending prefix wins. */
  decoding: Promise<void>;
  pending: { plain: Uint8Array; fraction: number; durationHint: number | null } | null;
}

const entries = new Map<string, Entry>();

export function waveformCacheKey(uploadId: string, version: number | null | undefined): string {
  return `${uploadId}:${version ?? 0}`;
}

/** Current state for a file, `undefined` when nothing has been computed yet. */
export function peekWaveform(key: string): WaveformState | undefined {
  return entries.get(key)?.state;
}

export function invalidateWaveformPeaks(uploadId: string): void {
  for (const key of Array.from(entries.keys())) {
    if (key.startsWith(`${uploadId}:`)) entries.delete(key);
  }
}

export function subscribeWaveform(key: string, cb: Listener): () => void {
  const e = getEntry(key);
  e.listeners.add(cb);
  return () => { e.listeners.delete(cb); };
}

function getEntry(key: string): Entry {
  let e = entries.get(key);
  if (!e) {
    if (entries.size >= CACHE_MAX_ENTRIES) {
      const oldest = entries.keys().next().value;
      if (oldest !== undefined) entries.delete(oldest);
    }
    e = { state: { peaks: null, coverage: 0, final: false }, listeners: new Set(), decodedFraction: 0, decodedAt: 0, decoding: Promise.resolve(), pending: null };
    entries.set(key, e);
  }
  return e;
}

function setState(e: Entry, state: WaveformState): void {
  e.state = state;
  for (const l of Array.from(e.listeners)) {
    try { l(state); } catch { /* one listener's error must not break the others */ }
  }
}

/** Decide up front that a file gets no waveform (too big, too long). */
export function markNoWaveform(key: string): void {
  const e = getEntry(key);
  if (!e.state.final) setState(e, { peaks: null, coverage: 0, final: true });
}

/**
 * Feed the plaintext prefix available so far. `fraction` is the share of
 * the FILE's bytes that prefix is (1 = the whole file); `durationHint` is
 * the media length in seconds when known, which places the decoded prefix
 * on the timeline exactly (otherwise it's estimated from the byte share).
 * Cheap to call often: decodes are throttled and serialised.
 */
export function feedWaveformBytes(key: string, plain: Uint8Array, fraction: number, durationHint: number | null): void {
  const e = getEntry(key);
  if (e.state.final) return;
  const complete = fraction >= 1;
  if (!complete) {
    const due = e.decodedFraction === 0 ? fraction >= PROGRESSIVE_FIRST_STEP : fraction >= e.decodedFraction * PROGRESSIVE_GROWTH;
    if (!due || Date.now() - e.decodedAt < PROGRESSIVE_MIN_INTERVAL_MS) return;
  }
  e.pending = { plain, fraction, durationHint };
  e.decoding = e.decoding.then(async () => {
    const job = e.pending;
    e.pending = null;
    if (!job || e.state.final) return;
    if (job.fraction <= e.decodedFraction && job.fraction < 1) return;
    e.decodedFraction = job.fraction;
    e.decodedAt = Date.now();
    const decoded = await decodePrefix(job.plain);
    if (e.state.final) return;
    const isFinal = job.fraction >= 1;
    if (!decoded) {
      // A prefix the browser can't decode (index at the end of the file)
      // isn't a verdict — the complete file may still decode. Only a
      // failed decode of the whole thing is.
      if (isFinal) setState(e, { peaks: null, coverage: 0, final: true });
      return;
    }
    const total = job.durationHint && job.durationHint > 0
      ? job.durationHint
      : (job.fraction > 0 ? decoded.duration / job.fraction : decoded.duration);
    const coverage = isFinal ? 1 : Math.max(0, Math.min(1, total > 0 ? decoded.duration / total : job.fraction));
    const buckets = Math.max(1, Math.round(WAVEFORM_BUCKETS * coverage));
    const part = await reducePeaks(decoded.channels, buckets);
    const peaks = new Float32Array(WAVEFORM_BUCKETS);
    peaks.set(part.subarray(0, Math.min(part.length, WAVEFORM_BUCKETS)));
    setState(e, { peaks, coverage, final: isFinal });
  }).catch(() => {
    if (!e.state.final && fraction >= 1) setState(e, { peaks: e.state.peaks, coverage: e.state.coverage, final: true });
  });
}

// ── Worker plumbing ──────────────────────────────────────────────────────

let worker: Worker | null = null;
let workerBroken = false;
let nextRequestId = 1;
const pending = new Map<number, { resolve: (p: Float32Array) => void; reject: (e: Error) => void }>();

function getWorker(): Worker | null {
  if (workerBroken) return null;
  if (worker) return worker;
  try {
    // Must stay exactly `new Worker(new URL(literal, import.meta.url), …)`:
    // Vite statically discovers worker entries from this exact shape.
    worker = new Worker(new URL('../workers/waveformPeaksWorker.ts', import.meta.url), { type: 'module' });
    worker.onmessage = (e: MessageEvent<{ id: number; peaks?: Float32Array; error?: string }>) => {
      const req = pending.get(e.data.id);
      if (!req) return;
      pending.delete(e.data.id);
      if (e.data.peaks) req.resolve(e.data.peaks);
      else req.reject(new Error(e.data.error || 'peaks worker failed'));
    };
    worker.onerror = () => {
      // Fail every waiter, retire the worker, and let later calls fall back
      // to the main-thread reducer rather than retrying a broken script.
      for (const req of pending.values()) req.reject(new Error('peaks worker crashed'));
      pending.clear();
      try { worker?.terminate(); } catch { /* ignore */ }
      worker = null;
      workerBroken = true;
    };
    return worker;
  } catch {
    workerBroken = true;
    return null;
  }
}

function reducePeaks(channels: Float32Array[], buckets: number): Promise<Float32Array> {
  const w = getWorker();
  if (!w) return Promise.resolve(computePeaks(channels, buckets));
  return new Promise<Float32Array>((resolve, reject) => {
    const id = nextRequestId++;
    pending.set(id, { resolve, reject });
    try {
      w.postMessage({ id, channels, buckets }, channels.map(c => c.buffer));
    } catch (err) {
      pending.delete(id);
      reject(err instanceof Error ? err : new Error(String(err)));
    }
  }).catch(() => computePeaks(channels, buckets));
}

// ── Decode ───────────────────────────────────────────────────────────────

function makeOfflineContext(): OfflineAudioContext | null {
  const Ctor = (window as unknown as { OfflineAudioContext?: typeof OfflineAudioContext; webkitOfflineAudioContext?: typeof OfflineAudioContext })
    .OfflineAudioContext
    ?? (window as unknown as { webkitOfflineAudioContext?: typeof OfflineAudioContext }).webkitOfflineAudioContext;
  if (!Ctor) return null;
  // Some engines reject sub-CD sample rates; fall back to the native default
  // rather than skip the waveform entirely.
  try { return new Ctor(1, 1, DECODE_SAMPLE_RATE); } catch { /* try the default rate */ }
  try { return new Ctor(1, 1, 44100); } catch { return null; }
}

async function decodePrefix(plain: Uint8Array): Promise<{ channels: Float32Array[]; duration: number } | null> {
  const ctx = makeOfflineContext();
  if (!ctx || plain.byteLength === 0 || plain.byteLength > WAVEFORM_MAX_SOURCE_BYTES) return null;
  try {
    // decodeAudioData detaches the buffer it is given — hand it a private
    // copy so the caller's bytes (the video itself, the trim source) survive.
    const audio = await ctx.decodeAudioData(plain.slice().buffer);
    if (!audio || audio.length === 0) return null;
    const channels: Float32Array[] = [];
    for (let c = 0; c < audio.numberOfChannels; c++) channels.push(audio.getChannelData(c));
    return { channels, duration: audio.duration };
  } catch {
    return null; // no audio track, an undecodable prefix, or a codec this browser can't decode
  }
}
